import type { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import { FranchisePage } from '../../../../components/Journals';
import Layout from '../../../../components/Layout';
import { toCanonicalUrl } from '../../../../helpers/links';
import { imageOgStaticLoader } from '../../../../helpers/og-image';
import {
  FranchiseIndex,
  FranchiseIndexStories,
  Franchises,
  FranchisesStories,
} from '../../../../types/Franchises';
import { castArray } from '../../../../utils/arrays';
import { getStoryblokPage } from '../../../../utils/storyblok';
import { pageTranslations } from '../../../../utils/translations';

type Props = {
  franchise: string;
  locale: string;
  locales: string[];
  index: FranchiseIndex;
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  stories: Franchises[];
  ogImageUrl: string;
};

const Page = ({
  franchise,
  index,
  locale,
  locales,
  ogImageUrl,
  pagination,
  stories,
}: Props) => {
  return (
    <Layout
      canonicalUrl={toCanonicalUrl({
        path: `/insights/${franchise}/1`,
        locale,
        hostname: String(process.env.NEXT_PUBLIC_HOSTNAME),
      })}
      description={index.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={index.content.seo?.title}
    >
      <FranchisePage
        franchise={franchise}
        franchiseIndex={index}
        locale={locale}
        pagination={pagination}
        stories={stories}
      />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({
  locale,
  locales,
  params,
  preview = false,
}) => {
  const translations = await pageTranslations(locale || 'en', [
    'insights',
    'og',
  ]);
  const [franchise] = castArray(params?.franchise);
  const [number] = castArray(params?.number || '1');

  const pagination = {
    page: Number(number),
    perPage: 16,
  };

  const indexStories: FranchiseIndexStories | null = await getStoryblokPage(
    'cdn/stories',
    {
      locale,
      preview,
      starts_with: `insights/${franchise}/`,
      is_startpage: true,
    },
  );

  if (!indexStories?.data.stories.length) {
    return {
      notFound: true,
    };
  }

  const stories: FranchisesStories | null = await getStoryblokPage(
    'cdn/stories',
    {
      locale,
      preview,
      page: pagination.page,
      per_page: pagination.perPage,
      sort_by: 'content.date:desc',
      starts_with: `insights/${franchise}/`,
      is_startpage: false,
    },
  );

  const index = indexStories.data.stories[0];

  const ogImageUrl = await imageOgStaticLoader({
    overlineKey: { key: 'og:insights.overline' },
    titleKey: index.content.title,
    imageSrc: index.content.subscription[0]?.image.filename,
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      locale,
      locales,
      franchise,
      ogImageUrl,
      pagination: {
        ...pagination,
        total: stories?.total || 0,
      },
      index,
      stories: stories?.data.stories || [],
      ...translations,
    },
  };
};

export const getStaticPaths: GetStaticPaths = () => ({
  fallback: 'blocking',
  paths: [],
});

export default Page;
